import { useTranslation } from '../../../i18n/useTranslation';
import {
    getBuyerContacts,
    assignContractorContact,
    addContractorContact,
    updateOrAddBuyerContact,
} from '../../../components/shopping-cart/services/shopping-cart.service';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import {
    Dialog,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    IconButton,
    Button,
    Box,
    DialogTitle,
} from '@mui/material';
import BuildingSitesListSkeleton from '../../building-sites-list/building-sites-list-skeleton';
import { IBuyerContactListView } from '../../../components/order/interfaces/buyer-contact-list-view.interface';
import SelectContactDialog from './select-contact-dialog';
import AddContractorContactDialog, { NewContractorPersonFormValues } from './add-contractor-contact-dialog';
import { Utils } from '../../../shared/constants/utilities.constants';

interface Prop {
    isOpen: boolean;
    handleCloseDialog: Function;
    selectedBuildingSiteUuid: string;
    parentContractorId: string;
}

const BuildingSitesDeliveryContactSelector: React.FC<Prop> = ({
    isOpen,
    selectedBuildingSiteUuid,
    parentContractorId,
    handleCloseDialog,
}) => {
    const { t } = useTranslation('translation');
    const [page] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [buyerContacts, setBuyerContacts] = useState<IBuyerContactListView[]>([]);
    const [isSelectContactDialogOpen, setIsSelectContactDialogOpen] = useState<boolean>(false);
    const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState<boolean>(false);

    const handleClose = (selectedBuyerContact: IBuyerContactListView | null) => {
        handleCloseDialog(selectedBuyerContact);
    };

    const getAndSetBuyerContacts = (page: number) => {
        setIsLoading(true);
        getBuyerContacts(selectedBuildingSiteUuid, page).then((response) => {
            if (response) {
                const data: IBuyerContactListView[] = response.data;
                setBuyerContacts(data);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        });
    };

    useEffect(() => {
        getAndSetBuyerContacts(page);
    }, []);

    const handleSelectContactDialogClose = (selectedContact: IBuyerContactListView | null) => {
        setIsSelectContactDialogOpen(false);
        if (!selectedContact?.id) {
            return;
        }
        assignContractorContact({
            originalContractorId: selectedBuildingSiteUuid,
            assignedContact: {
                assignedContractorId: parentContractorId,
                contactId: selectedContact.id,
            },
        }).then((response) => {
            if (response) {
                updateOrAddBuyerContact(selectedContact).then(() => {
                    getAndSetBuyerContacts(page);
                });
            }
        });
    };

    const handleAddContactDialogClose = (newContact: NewContractorPersonFormValues | null) => {
        setIsAddContactDialogOpen(false);
        if (!newContact) {
            return;
        }
        addContractorContact({
            contractorId: selectedBuildingSiteUuid,
            contractorPerson: newContact,
        }).then((response) => {
            if (response) {
                const buyerContact = {
                    id: newContact.id,
                    contactTypeId: newContact.contactTypeId,
                    objectId: selectedBuildingSiteUuid,
                    title: newContact.title,
                    firstName: newContact.firstName,
                    lastName: newContact.lastName,
                    phone: newContact.phone,
                    mobile: newContact.mobile,
                    email: newContact.email,
                    country: newContact.country,
                } as IBuyerContactListView;
                updateOrAddBuyerContact(buyerContact).then(() => {
                    getAndSetBuyerContacts(page);
                });
            }
        });
    };

    return (
        <div>
            <Dialog open={isOpen} onClose={() => handleClose(null)} fullWidth maxWidth="md">
                <DialogTitle
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pr: 7,
                    }}
                >
                    <Typography variant="h6">{t('SELECT_DELIVERY_CONTACT')}</Typography>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{
                                textTransform: 'capitalize',
                            }}
                            disabled={Utils.defaultUuid === parentContractorId}
                            onClick={() => setIsSelectContactDialogOpen(true)}
                        >
                            {t('ASSIGN_CONTACT')}
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            sx={{
                                textTransform: 'capitalize',
                            }}
                            onClick={() => setIsAddContactDialogOpen(true)}
                        >
                            {t('ADD_ANOTHER_CONTACT')}
                        </Button>
                    </Box>

                    <IconButton
                        onClick={() => handleClose(null)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ position: 'relative', mt: 1 }}>
                    {isLoading ? (
                        <BuildingSitesListSkeleton count={3} />
                    ) : (
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('FIRST_NAME')}</strong>
                                        </TableCell>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('LAST_NAME')}</strong>
                                        </TableCell>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('PHONE')}</strong>
                                        </TableCell>
                                        <TableCell
                                            sx={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}
                                        >
                                            <strong>{t('EMAIL')}</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {buyerContacts.map((item) => (
                                        <TableRow
                                            onClick={() => {
                                                handleClose(item);
                                            }}
                                            key={item.id}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: '#f2f2f2',
                                                    cursor: 'pointer',
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {item.firstName}
                                            </TableCell>
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {item.lastName}
                                            </TableCell>
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {item.phone}
                                            </TableCell>
                                            <TableCell sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {item.email}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>

            <SelectContactDialog
                isOpen={isSelectContactDialogOpen}
                parentContractorId={parentContractorId}
                handleCloseDialog={handleSelectContactDialogClose}
            />

            <AddContractorContactDialog
                isOpen={isAddContactDialogOpen}
                handleCloseDialog={handleAddContactDialogClose}
            />
        </div>
    );
};
export default BuildingSitesDeliveryContactSelector;
